import React, { useMemo } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Modal,
  IconButton,
} from '@mui/material'
import {
  Description as DocumentIcon,
  MonetizationOn as FundingIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { useList } from '@refinedev/core'
import { format } from 'date-fns'
import { UUID } from 'crypto'
import FundingNeedScanModal from './FundingNeedScanModal'
import axios from 'axios'
import { IwiData } from '@/interfaces/iwi'

interface OrgProfileProps {
  orgUuid: UUID
  orgData: any
  isFetchingOrg: boolean
}

interface FundingNeed {
  id: string
  title: string
  amount: number
  status: string
  funding_confirm_by_start: string
  description?: string
  region_ids?: number[]
  profile_text?: string
  timing_criteria?: string
  iwi?: number[] | null
  accepts_gambling_proceeds?: boolean
  accepts_international?: boolean
  is_archived?: boolean
}

interface Application {
  id: number
  status: string
  created_at: string
  need_id: number
  funder_name?: string
  opportunity_title?: string
}

interface OrgDoc {
  id: number
  doc_url: string
  date_added: string
  file_extension: string
  website_url?: string
}

interface Region {
  id: number
  name: string
  country: string
  region?: string
}

interface Iwi {
  id: number
  name: string
}

export default function OrgProfile({
  orgUuid,
  orgData,
  isFetchingOrg,
}: OrgProfileProps) {
  // Fetch org documents
  const { data: docsData, isLoading: docsLoading } = useList<OrgDoc>({
    resource: 'orgs',
    meta: {
      orgUuid: orgUuid,
      endpoint: 'docs',
    },
    queryOptions: {
      enabled: !!orgUuid,
    },
  })

  // Fetch funding needs for this org
  const { data: fundingNeedsData, isLoading: fundingNeedsLoading } =
    useList<FundingNeed>({
      resource: 'funding-needs/organisation',
      meta: {
        orgUuid: orgUuid,
      },
      queryOptions: {
        enabled: !!orgUuid,
      },
    })

  // Fetch org regions directly
  const { data: orgRegionsData, isLoading: regionsLoading } = useList<Region>({
    resource: 'orgs',
    meta: {
      orgUuid: orgUuid,
      endpoint: 'regions',
    },
    queryOptions: {
      enabled: !!orgUuid,
    },
  })

  // Fetch all regions data to map region IDs to names for funding needs
  const { data: allRegionsData } = useList<Region>({
    resource: 'regions',
    pagination: { pageSize: 5000, mode: 'off' },
  })

  // Fetch all regions data to map region IDs to names for funding needs
  const { data: allIwiData } = useList<Iwi>({
    resource: 'iwi',
    pagination: { pageSize: 500, mode: 'off' },
  })

  const { data: orgIwiNamesData, isLoading: orgIwiNamesLoading } =
    useList<IwiData>({
      resource: 'iwi',
      pagination: { pageSize: 100 },
      filters: [
        {
          field: 'id',
          operator: 'eq',
          value: orgData?.iwi,
        },
      ],
      queryOptions: {
        enabled: !!orgData?.iwi && orgData?.iwi.length > 0 && !isFetchingOrg,
      },

      errorNotification: (error) => {
        return {
          message: 'Error getting iwi names',
          description:
            error?.message || 'An error occurred while fetching iwi names',
          type: 'error',
        }
      },
    })

  // Fetch applications for this org
  const { data: applicationsData, isLoading: applicationsLoading } =
    useList<Application>({
      resource: 'applications/org',
      meta: {
        orgUuid: orgUuid,
      },
      queryOptions: {
        enabled: !!orgUuid,
      },
    })

  const docs = docsData?.data || []
  const fundingNeeds = fundingNeedsData?.data || []
  const allRegions = allRegionsData?.data || []
  const allIwi = allIwiData?.data || []
  const orgRegions = orgRegionsData?.data || []
  const orgIwi = orgIwiNamesData?.data || []
  const applications = applicationsData?.data || []

  // lookup maps for iwi and regions
  const regionMap = useMemo(() => {
    if (!allRegions || allRegions.length === 0) return new Map()
    return new Map(allRegions.map((region) => [region.id, region]))
  }, [allRegions])

  const iwiMap = useMemo(() => {
    if (!allIwi || allIwi.length === 0) return new Map()
    return new Map(allIwi.map((iwi) => [iwi.id, iwi]))
  }, [allIwi])

  // Helper function to get first 50 words of description
  const getDescriptionPreview = (description: string) => {
    if (!description) return 'No description available'
    const words = description.split(' ')
    if (words.length <= 50) return description
    return words.slice(0, 50).join(' ') + '...'
  }

  const [showProfileModal, setShowProfileModal] = React.useState(false)
  const [expandedDescriptions, setExpandedDescriptions] = React.useState<
    Set<string>
  >(new Set())
  const [scanModalOpen, setScanModalOpen] = React.useState(false)
  const [selectedNeedForScan, setSelectedNeedForScan] = React.useState<{
    id: string
    title: string
  } | null>(null)
  const [scanCounts, setScanCounts] = React.useState<
    Record<string, { total: number; highQuality: number }>
  >({})

  const toggleDescription = (needId: string) => {
    const newExpanded = new Set(expandedDescriptions)
    if (newExpanded.has(needId)) {
      newExpanded.delete(needId)
    } else {
      newExpanded.add(needId)
    }
    setExpandedDescriptions(newExpanded)
  }

  const isDescriptionExpanded = (needId: string) =>
    expandedDescriptions.has(needId)

  const handleViewScan = (needId: string, needTitle: string) => {
    setSelectedNeedForScan({ id: needId, title: needTitle })
    setScanModalOpen(true)
  }

  const handleCloseScanModal = () => {
    setScanModalOpen(false)
    setSelectedNeedForScan(null)
  }

  // Fetch scan counts for all funding needs
  const fetchScanCounts = React.useCallback(async () => {
    if (!fundingNeeds.length) return

    const counts: Record<string, { total: number; highQuality: number }> = {}

    try {
      const apiUrl = import.meta.env.VITE_API_URL

      // Fetch scan counts for each funding need
      for (const need of fundingNeeds) {
        try {
          const response = await axios.get(`${apiUrl}/scans/`, {
            params: {
              need_id: need.id,
              _start: 0,
              _end: 1,
              _sort: 'scan_date',
              _order: 'desc',
              timeframe: 'all',
            },
          })

          if (response.data && response.data.length > 0) {
            const latestScan = response.data[0]
            counts[need.id] = {
              total: latestScan.total_results || 0,
              highQuality: latestScan.high_ranking_results || 0,
            }
          } else {
            counts[need.id] = { total: 0, highQuality: 0 }
          }
        } catch (err) {
          console.error(`Failed to fetch scan counts for need ${need.id}:`, err)
          counts[need.id] = { total: 0, highQuality: 0 }
        }
      }

      setScanCounts(counts)
    } catch (error) {
      console.error('Failed to fetch scan counts:', error)
    }
  }, [fundingNeeds])

  // Fetch scan counts when funding needs are loaded
  React.useEffect(() => {
    if (fundingNeeds.length > 0) {
      fetchScanCounts()
    }
  }, [fetchScanCounts])


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Details Section */}
      <Card>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6" fontWeight={600}>
              Details
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ViewIcon />}
              onClick={() => setShowProfileModal(true)}
            >
              View Profile
            </Button>
          </Box>

          {/* Organisation Description Preview */}
          <Box mb={2}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Description:
              </Typography>
              {docsLoading ? (
                <CircularProgress size={16} />
              ) : (
                <Chip
                  icon={<DocumentIcon />}
                  label={`${docs.length} documents`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {getDescriptionPreview(orgData?.description || '')}
            </Typography>
          </Box>

          {/* Organisation Locations */}
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              Locations:
            </Typography>
            {regionsLoading ? (
              <CircularProgress size={16} />
            ) : orgRegions.length > 0 ? (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {orgRegions.map((region: Region) => (
                  <Chip
                    key={region.id}
                    label={`${region.name}${
                      region.country ? `, ${region.country}` : ''
                    }`}
                    variant="outlined"
                    color="primary"
                    size="small"
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No locations assigned
              </Typography>
            )}
          </Box>

          {/* Organisation Iwi */}
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              Iwi:
            </Typography>
            {orgIwiNamesLoading && orgData?.iwi && orgData?.iwi.length > 0 ? (
              <CircularProgress size={16} />
            ) : orgIwi.length > 0 ? (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {(orgIwi as Iwi[]).map((iwi: Iwi) => (
                  <Chip
                    key={iwi.id}
                    label={iwi.name}
                    variant="outlined"
                    color="warning"
                    size="small"
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                None
              </Typography>
            )}
          </Box>

          {/* Organisation Attributes */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              Organisation Attributes:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              <Chip
                label={`Accepts Gambling Proceeds: ${
                  orgData?.accepts_gambling_proceeds ? 'Yes' : 'No'
                }`}
                size="small"
                variant="outlined"
                color={
                  orgData?.accepts_gambling_proceeds ? 'success' : 'default'
                }
              />
              <Chip
                label={`Accepts International Funding: ${
                  orgData?.accepts_international ? 'Yes' : 'No'
                }`}
                size="small"
                variant="outlined"
                color={orgData?.accepts_international ? 'success' : 'default'}
              />
            </Box>
          </Box>

          {/* Match Preferences */}
          {orgData?.match_preferences?.filter(
            (p: { need_id: number | null }) => p.need_id == null,
          ).length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                Organisation Match Preferences:
              </Typography>
              {orgData.match_preferences
                .filter(
                  (p: { need_id: number | null }) => p.need_id == null,
                )
                .map(
                  (pref: { id: number; preference_text: string }) => (
                    <Typography
                      key={pref.id}
                      variant="body2"
                      sx={{
                        p: 1,
                        mb: 0.5,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {pref.preference_text}
                    </Typography>
                  ),
                )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Profile Modal */}
      <Modal
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        aria-labelledby="org-profile-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '90%',
            maxWidth: 800,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'auto',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h5" fontWeight={600}>
              {orgData?.name || 'Organisation'} - Funding Profile
            </Typography>
            <IconButton onClick={() => setShowProfileModal(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Modal Content */}
          <Box sx={{ p: 3 }}>
            {orgData?.profile_text ? (
              <Box>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}
                >
                  {orgData.profile_text}
                </Typography>
              </Box>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography variant="h6" color="text.secondary" mb={2}>
                  No Funding Profile Available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This organisation has not yet created a funding profile for
                  matching opportunities.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Funding Needs */}
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <FundingIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Funding Needs
            </Typography>
            <Chip
              label={`${fundingNeeds.length} needs`}
              size="small"
              color="warning"
            />
          </Box>
          {fundingNeedsLoading ? (
            <CircularProgress size={20} />
          ) : fundingNeeds.length > 0 ? (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Active funding needs for this organisation:
              </Typography>
              {fundingNeeds.map((need) => (
                <Card key={need.id} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Box mb={2}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={1}
                      >
                        <Typography variant="h6" fontWeight={600}>
                          {need.title || `Funding Need ${need.id}`}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          color="secondary"
                          sx={{ textTransform: 'none', flexShrink: 0, ml: 2 }}
                          onClick={() =>
                            handleViewScan(
                              need.id,
                              need.title || `Funding Need ${need.id}`,
                            )
                          }
                        >
                          {scanCounts[need.id]
                            ? `View Scan Results (${
                                scanCounts[need.id].highQuality
                              }/${scanCounts[need.id].total})`
                            : 'View Scan Results'}
                        </Button>
                      </Box>
                      <Box display="flex" gap={2} mb={2}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Amount:</strong> $
                          {need.amount?.toLocaleString() || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Status:</strong> {need.status || 'Active'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Funding Start:</strong>{' '}
                          {need.funding_confirm_by_start
                            ? format(
                                new Date(need.funding_confirm_by_start),
                                'MMM d, yyyy',
                              )
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </Box>

                    {need.description && (
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">
                          {isDescriptionExpanded(need.id)
                            ? need.description
                            : getDescriptionPreview(need.description)}
                        </Typography>
                        {need.description.split(' ').length > 50 && (
                          <Button
                            size="small"
                            onClick={() => toggleDescription(need.id)}
                            sx={{
                              mt: 1,
                              p: 0,
                              minWidth: 'auto',
                              textTransform: 'none',
                            }}
                          >
                            {isDescriptionExpanded(need.id)
                              ? 'Show less'
                              : 'See all'}
                          </Button>
                        )}
                      </Box>
                    )}

                    {/* Funding Need Info */}
                    <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                      {/* Regions for this funding need */}
                      {need.region_ids && need.region_ids.length > 0 && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            mb={1}
                          >
                            Targeted Locations:
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {need.region_ids.map((regionId) => {
                              const region = regionMap.get(regionId)
                              return region ? (
                                <Chip
                                  key={regionId}
                                  label={`${region.name}${
                                    region.country ? `, ${region.country}` : ''
                                  }`}
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                />
                              ) : null
                            })}
                          </Box>
                        </Box>
                      )}
                      {/* Iwi for this funding need */}
                      {need.iwi && need.iwi.length > 0 ? (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            mb={1}
                          >
                            Targeted Iwi:
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {need.iwi.map((iwiId) => {
                              const iwi = iwiMap.get(iwiId)
                              return iwi ? (
                                <Chip
                                  key={iwiId}
                                  label={iwi.name}
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                />
                              ) : null
                            })}
                          </Box>
                        </Box>
                      ) : need.iwi && need.iwi.length === 0 ? (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            mb={1}
                          >
                            Targeted Iwi:
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            <Chip
                              key={`${need.id}-no-iwi`}
                              label={'No Iwi'}
                              size="small"
                              variant="outlined"
                              color="warning"
                            />
                          </Box>
                        </Box>
                      ) : null}

                      {/* Document count for this funding need */}
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} mb={1}>
                          Documents:
                        </Typography>
                        <Chip
                          label={`${docs.length} total docs`}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      </Box>
                    </Box>

                    {/* Applications for this funding need */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} mb={1}>
                        Linked Applications:
                      </Typography>
                      {applicationsLoading ? (
                        <CircularProgress size={16} />
                      ) : (
                        (() => {
                          const needApplications = applications.filter(
                            (app) => app.need_id === parseInt(need.id),
                          )
                          return needApplications.length > 0 ? (
                            <Box display="flex" flexWrap="wrap" gap={1}>
                              {needApplications.map((app) => (
                                <Chip
                                  key={app.id}
                                  label={`${
                                    app.funder_name
                                      ? `${app.funder_name} - `
                                      : ''
                                  }${
                                    app.opportunity_title || `App #${app.id}`
                                  } (${app.status || 'Pending'})`}
                                  size="small"
                                  variant="outlined"
                                  color={
                                    app.status === 'approved'
                                      ? 'success'
                                      : app.status === 'rejected'
                                      ? 'error'
                                      : 'default'
                                  }
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No applications submitted yet
                            </Typography>
                          )
                        })()
                      )}
                    </Box>

                    {/* Need-specific preferences */}
                    {orgData?.match_preferences?.filter(
                      (p: { need_id: number | null }) =>
                        p.need_id === parseInt(need.id),
                    ).length > 0 && (
                      <Box mt={2}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          mb={1}
                        >
                          Specific Match Preferences:
                        </Typography>
                        {orgData.match_preferences
                          .filter(
                            (p: { need_id: number | null }) =>
                              p.need_id === parseInt(need.id),
                          )
                          .map(
                            (pref: { id: number; preference_text: string }) => (
                              <Typography
                                key={pref.id}
                                variant="body2"
                                sx={{
                                  p: 1,
                                  mb: 0.5,
                                  bgcolor: 'grey.50',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                }}
                              >
                                {pref.preference_text}
                              </Typography>
                            ),
                          )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No funding needs found for this organisation
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Scan Results Modal */}
      {selectedNeedForScan && (
        <FundingNeedScanModal
          open={scanModalOpen}
          onClose={handleCloseScanModal}
          needId={selectedNeedForScan.id}
          needTitle={selectedNeedForScan.title}
        />
      )}
    </Box>
  )
}
